import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiFileText, 
  FiDownload, 
  FiDollarSign, 
  FiTrendingUp,
  FiUsers,
  FiBarChart2,
  FiPieChart,
  FiFilter
} from 'react-icons/fi';
import './BaakLaporan.css';

const BaakLaporan = () => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    pembayaran: [],
    tagihan: [],
    students: []
  });
  
  // Filter State
  const [filterType, setFilterType] = useState('bulanan'); // bulanan, semesteran, tahunan, custom
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedSemester, setSelectedSemester] = useState('Ganjil');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedJenisBiaya, setSelectedJenisBiaya] = useState('');

  // Calculated Stats
  const [stats, setStats] = useState({
    totalPendapatan: 0,
    totalTagihan: 0,
    totalTunggakan: 0,
    jumlahMahasiswaBayar: 0,
    jumlahMahasiswaBelumBayar: 0,
    persentasePelunasan: 0,
    pembayaranPerJenis: [],
    pembayaranPerProgram: [],
    trendPembayaran: []
  });

  const monthNames = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
  ];

  const jenisBiayaOptions = [
    'SPP',
    'Biaya Pendaftaran',
    'Biaya Praktikum',
    'UTS',
    'UAS',
    'Skripsi',
    'Wisuda',
    'Herregistrasi',
    'Lainnya'
  ];

  useEffect(() => {
    fetchReportData();
  }, []);

  useEffect(() => {
    if (reportData.pembayaran.length > 0 || reportData.tagihan.length > 0) {
      calculateStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData, filterType, selectedMonth, selectedYear, selectedSemester, customStartDate, customEndDate, selectedProgram, selectedJenisBiaya]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const [pembayaranRes, tagihanRes, studentsRes] = await Promise.all([
        axios.get('/api/finance/pembayaran-mahasiswa'),
        axios.get('/api/tagihan'),
        axios.get('/api/students', { params: { limit: 10000 } })
      ]);

      setReportData({
        pembayaran: pembayaranRes.data.data || [],
        tagihan: tagihanRes.data.data || [],
        students: studentsRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error(error.response?.data?.message || 'Gagal mengambil data laporan');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = () => {
    let filteredPembayaran = [...reportData.pembayaran];
    let filteredTagihan = [...reportData.tagihan];

    // Filter by date range based on filterType
    if (filterType === 'bulanan') {
      filteredPembayaran = filteredPembayaran.filter(p => {
        const date = new Date(p.tanggal_pembayaran);
        return date.getMonth() + 1 === parseInt(selectedMonth) && 
               date.getFullYear() === parseInt(selectedYear);
      });
      filteredTagihan = filteredTagihan.filter(t => {
        const date = new Date(t.deadline);
        return date.getMonth() + 1 === parseInt(selectedMonth) && 
               date.getFullYear() === parseInt(selectedYear);
      });
    } else if (filterType === 'semesteran') {
      filteredPembayaran = filteredPembayaran.filter(p => {
        const date = new Date(p.tanggal_pembayaran);
        return date.getFullYear() === parseInt(selectedYear);
      });
      filteredTagihan = filteredTagihan.filter(t => {
        return t.semester === selectedSemester && 
               t.tahun_ajaran.includes(selectedYear.toString());
      });
    } else if (filterType === 'tahunan') {
      filteredPembayaran = filteredPembayaran.filter(p => {
        const date = new Date(p.tanggal_pembayaran);
        return date.getFullYear() === parseInt(selectedYear);
      });
      filteredTagihan = filteredTagihan.filter(t => {
        return t.tahun_ajaran.includes(selectedYear.toString());
      });
    } else if (filterType === 'custom' && customStartDate && customEndDate) {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      filteredPembayaran = filteredPembayaran.filter(p => {
        const date = new Date(p.tanggal_pembayaran);
        return date >= start && date <= end;
      });
      filteredTagihan = filteredTagihan.filter(t => {
        const date = new Date(t.deadline);
        return date >= start && date <= end;
      });
    }

    // Filter by program
    if (selectedProgram) {
      const nimsByProgram = reportData.students
        .filter(s => s.program === selectedProgram)
        .map(s => s.nim);
      
      filteredPembayaran = filteredPembayaran.filter(p => 
        nimsByProgram.includes(p.nim)
      );
      filteredTagihan = filteredTagihan.filter(t => 
        nimsByProgram.includes(t.nim)
      );
    }

    // Filter by jenis biaya
    if (selectedJenisBiaya) {
      filteredPembayaran = filteredPembayaran.filter(p => 
        p.jenis_biaya === selectedJenisBiaya
      );
      filteredTagihan = filteredTagihan.filter(t => 
        t.jenis_tagihan === selectedJenisBiaya
      );
    }

    return { filteredPembayaran, filteredTagihan };
  };

  const calculateStats = () => {
    const { filteredPembayaran, filteredTagihan } = getFilteredData();

    // Total Pendapatan
    const totalPendapatan = filteredPembayaran.reduce((sum, p) => 
      sum + (p.nominal_dibayar || 0), 0
    );

    // Total Tagihan
    const totalTagihan = filteredTagihan.reduce((sum, t) => 
      sum + (t.jumlah || 0), 0
    );

    // Total Tunggakan (tagihan belum lunas)
    const totalTunggakan = filteredTagihan
      .filter(t => t.status === 'belum_lunas')
      .reduce((sum, t) => sum + (t.jumlah || 0), 0);

    // Jumlah mahasiswa yang sudah bayar
    const nimsBayar = new Set(filteredPembayaran.map(p => p.nim));
    const jumlahMahasiswaBayar = nimsBayar.size;

    // Jumlah mahasiswa dengan tagihan belum lunas
    const nimsBelumBayar = new Set(
      filteredTagihan
        .filter(t => t.status === 'belum_lunas')
        .map(t => t.nim)
    );
    const jumlahMahasiswaBelumBayar = nimsBelumBayar.size;

    // Persentase pelunasan
    const tagihanLunas = filteredTagihan.filter(t => t.status === 'lunas').length;
    const persentasePelunasan = filteredTagihan.length > 0 
      ? Math.round((tagihanLunas / filteredTagihan.length) * 100) 
      : 0;

    // Pembayaran per jenis
    const pembayaranPerJenis = {};
    filteredPembayaran.forEach(p => {
      const jenis = p.jenis_biaya || 'Lainnya';
      if (!pembayaranPerJenis[jenis]) {
        pembayaranPerJenis[jenis] = 0;
      }
      pembayaranPerJenis[jenis] += p.nominal_dibayar || 0;
    });

    // Pembayaran per program
    const pembayaranPerProgram = {};
    filteredPembayaran.forEach(p => {
      const student = reportData.students.find(s => s.nim === p.nim);
      const program = student?.program || 'Unknown';
      if (!pembayaranPerProgram[program]) {
        pembayaranPerProgram[program] = 0;
      }
      pembayaranPerProgram[program] += p.nominal_dibayar || 0;
    });

    // Trend pembayaran (per hari untuk bulan ini)
    const trendPembayaran = [];
    if (filterType === 'bulanan') {
      const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
      for (let day = 1; day <= daysInMonth; day++) {
        const dayPembayaran = filteredPembayaran.filter(p => {
          const date = new Date(p.tanggal_pembayaran);
          return date.getDate() === day;
        });
        const total = dayPembayaran.reduce((sum, p) => sum + (p.nominal_dibayar || 0), 0);
        trendPembayaran.push({
          label: day.toString(),
          value: total
        });
      }
    } else if (filterType === 'tahunan') {
      // Trend per bulan
      for (let month = 1; month <= 12; month++) {
        const monthPembayaran = filteredPembayaran.filter(p => {
          const date = new Date(p.tanggal_pembayaran);
          return date.getMonth() + 1 === month;
        });
        const total = monthPembayaran.reduce((sum, p) => sum + (p.nominal_dibayar || 0), 0);
        trendPembayaran.push({
          label: monthNames[month - 1].substring(0, 3),
          value: total
        });
      }
    }

    setStats({
      totalPendapatan,
      totalTagihan,
      totalTunggakan,
      jumlahMahasiswaBayar,
      jumlahMahasiswaBelumBayar,
      persentasePelunasan,
      pembayaranPerJenis: Object.entries(pembayaranPerJenis).map(([jenis, total]) => ({
        jenis,
        total
      })),
      pembayaranPerProgram: Object.entries(pembayaranPerProgram).map(([program, total]) => ({
        program,
        total
      })),
      trendPembayaran
    });
  };

  const formatRupiah = (angka) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(angka);
  };

  const handleExportPDF = () => {
    toast.info('Fitur export PDF sedang dalam pengembangan');
    // TODO: Implement PDF export
  };

  const handleExportExcel = () => {
    toast.info('Fitur export Excel sedang dalam pengembangan');
    // TODO: Implement Excel export
  };

  const handlePrint = () => {
    window.print();
  };

  const getReportTitle = () => {
    if (filterType === 'bulanan') {
      return `Laporan Keuangan ${monthNames[selectedMonth - 1]} ${selectedYear}`;
    } else if (filterType === 'semesteran') {
      return `Laporan Keuangan Semester ${selectedSemester} ${selectedYear}`;
    } else if (filterType === 'tahunan') {
      return `Laporan Keuangan Tahun ${selectedYear}`;
    } else if (filterType === 'custom') {
      return `Laporan Keuangan ${customStartDate} - ${customEndDate}`;
    }
    return 'Laporan Keuangan';
  };

  const uniquePrograms = [...new Set(reportData.students.map(s => s.program))].filter(Boolean);

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="baak-laporan-page">
      {/* Header */}
      <div className="modern-header">
        <div className="header-background">
          <div className="header-pattern"></div>
        </div>
        <div className="header-content">
          <div className="header-text">
            <div className="header-icon-wrapper">
              <FiBarChart2 className="header-icon" />
            </div>
            <div>
              <h1>Laporan Keuangan</h1>
              <p>Analisis dan statistik pembayaran mahasiswa</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn-glass" onClick={handlePrint}>
              <FiFileText /> Print
            </button>
            <button className="btn-glass" onClick={handleExportExcel}>
              <FiDownload /> Export Excel
            </button>
            <button className="btn-glass" onClick={handleExportPDF}>
              <FiDownload /> Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card filter-card">
        <div className="filter-header">
          <h3><FiFilter /> Filter Laporan</h3>
        </div>
        <div className="filter-grid">
          <div className="filter-group">
            <label>Tipe Laporan</label>
            <select 
              className="form-control"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="bulanan">Bulanan</option>
              <option value="semesteran">Semesteran</option>
              <option value="tahunan">Tahunan</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {filterType === 'bulanan' && (
            <>
              <div className="filter-group">
                <label>Bulan</label>
                <select 
                  className="form-control"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index + 1}>{month}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Tahun</label>
                <input 
                  type="number"
                  className="form-control"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  min="2020"
                  max="2030"
                />
              </div>
            </>
          )}

          {filterType === 'semesteran' && (
            <>
              <div className="filter-group">
                <label>Semester</label>
                <select 
                  className="form-control"
                  value={selectedSemester}
                  onChange={(e) => setSelectedSemester(e.target.value)}
                >
                  <option value="Ganjil">Ganjil</option>
                  <option value="Genap">Genap</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Tahun</label>
                <input 
                  type="number"
                  className="form-control"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  min="2020"
                  max="2030"
                />
              </div>
            </>
          )}

          {filterType === 'tahunan' && (
            <div className="filter-group">
              <label>Tahun</label>
              <input 
                type="number"
                className="form-control"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                min="2020"
                max="2030"
              />
            </div>
          )}

          {filterType === 'custom' && (
            <>
              <div className="filter-group">
                <label>Tanggal Mulai</label>
                <input 
                  type="date"
                  className="form-control"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label>Tanggal Akhir</label>
                <input 
                  type="date"
                  className="form-control"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="filter-group">
            <label>Program Studi</label>
            <select 
              className="form-control"
              value={selectedProgram}
              onChange={(e) => setSelectedProgram(e.target.value)}
            >
              <option value="">Semua Program</option>
              {uniquePrograms.map(program => (
                <option key={program} value={program}>{program}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Jenis Biaya</label>
            <select 
              className="form-control"
              value={selectedJenisBiaya}
              onChange={(e) => setSelectedJenisBiaya(e.target.value)}
            >
              <option value="">Semua Jenis</option>
              {jenisBiayaOptions.map(jenis => (
                <option key={jenis} value={jenis}>{jenis}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Report Title */}
      <div className="report-title">
        <h2>{getReportTitle()}</h2>
        <p className="report-date">Dicetak pada: {new Date().toLocaleDateString('id-ID', { 
          day: 'numeric', 
          month: 'long', 
          year: 'numeric' 
        })}</p>
      </div>

      {/* Main Stats */}
      <div className="stats-main-grid">
        <div className="stat-card-large stat-primary">
          <div className="stat-icon-large">
            <FiDollarSign />
          </div>
          <div className="stat-content">
            <h4>Total Pendapatan</h4>
            <h2>{formatRupiah(stats.totalPendapatan)}</h2>
            <p>Total pembayaran yang diterima</p>
          </div>
        </div>

        <div className="stat-card-large stat-warning">
          <div className="stat-icon-large">
            <FiFileText />
          </div>
          <div className="stat-content">
            <h4>Total Tagihan</h4>
            <h2>{formatRupiah(stats.totalTagihan)}</h2>
            <p>Total tagihan yang dikeluarkan</p>
          </div>
        </div>

        <div className="stat-card-large stat-danger">
          <div className="stat-icon-large">
            <FiTrendingUp />
          </div>
          <div className="stat-content">
            <h4>Total Tunggakan</h4>
            <h2>{formatRupiah(stats.totalTunggakan)}</h2>
            <p>Tagihan yang belum dibayar</p>
          </div>
        </div>

        <div className="stat-card-large stat-success">
          <div className="stat-icon-large">
            <FiUsers />
          </div>
          <div className="stat-content">
            <h4>Pelunasan</h4>
            <h2>{stats.persentasePelunasan}%</h2>
            <p>{stats.jumlahMahasiswaBayar} dari {stats.jumlahMahasiswaBayar + stats.jumlahMahasiswaBelumBayar} mahasiswa</p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        {/* Pembayaran Per Jenis */}
        <div className="card chart-card">
          <div className="chart-header">
            <h3><FiPieChart /> Pembayaran per Jenis Biaya</h3>
          </div>
          <div className="chart-content">
            {stats.pembayaranPerJenis.length > 0 ? (
              <div className="horizontal-bar-chart">
                {stats.pembayaranPerJenis
                  .sort((a, b) => b.total - a.total)
                  .map((item, index) => {
                    const maxValue = Math.max(...stats.pembayaranPerJenis.map(i => i.total));
                    const percentage = (item.total / maxValue) * 100;
                    return (
                      <div key={index} className="bar-item">
                        <div className="bar-label">
                          <span className="bar-name">{item.jenis}</span>
                          <span className="bar-value">{formatRupiah(item.total)}</span>
                        </div>
                        <div className="bar-wrapper">
                          <div 
                            className="bar-fill" 
                            style={{ 
                              width: `${percentage}%`,
                              background: `linear-gradient(90deg, #667eea ${percentage}%, #764ba2 100%)`
                            }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="no-data">Tidak ada data pembayaran</div>
            )}
          </div>
        </div>

        {/* Pembayaran Per Program */}
        <div className="card chart-card">
          <div className="chart-header">
            <h3><FiBarChart2 /> Pembayaran per Program Studi</h3>
          </div>
          <div className="chart-content">
            {stats.pembayaranPerProgram.length > 0 ? (
              <div className="horizontal-bar-chart">
                {stats.pembayaranPerProgram
                  .sort((a, b) => b.total - a.total)
                  .map((item, index) => {
                    const maxValue = Math.max(...stats.pembayaranPerProgram.map(i => i.total));
                    const percentage = (item.total / maxValue) * 100;
                    return (
                      <div key={index} className="bar-item">
                        <div className="bar-label">
                          <span className="bar-name">{item.program}</span>
                          <span className="bar-value">{formatRupiah(item.total)}</span>
                        </div>
                        <div className="bar-wrapper">
                          <div 
                            className="bar-fill" 
                            style={{ 
                              width: `${percentage}%`,
                              background: `linear-gradient(90deg, #10b981 ${percentage}%, #059669 100%)`
                            }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="no-data">Tidak ada data pembayaran</div>
            )}
          </div>
        </div>
      </div>

      {/* Trend Chart */}
      {stats.trendPembayaran.length > 0 && (
        <div className="card chart-card-full">
          <div className="chart-header">
            <h3><FiTrendingUp /> Trend Pembayaran</h3>
          </div>
          <div className="chart-content">
            <div className="line-chart">
              {stats.trendPembayaran.map((item, index) => {
                const maxValue = Math.max(...stats.trendPembayaran.map(i => i.value), 1);
                const height = (item.value / maxValue) * 100;
                return (
                  <div key={index} className="line-chart-bar">
                    <div className="bar-value-top">{item.value > 0 ? formatRupiah(item.value) : ''}</div>
                    <div 
                      className="bar-column" 
                      style={{ height: `${height}%` }}
                      title={formatRupiah(item.value)}
                    ></div>
                    <div className="bar-label-bottom">{item.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Summary Table */}
      <div className="card">
        <div className="table-header">
          <h3>Ringkasan Detail</h3>
        </div>
        <div className="summary-table">
          <table>
            <tbody>
              <tr>
                <td className="summary-label">Total Pendapatan</td>
                <td className="summary-value">{formatRupiah(stats.totalPendapatan)}</td>
              </tr>
              <tr>
                <td className="summary-label">Total Tagihan</td>
                <td className="summary-value">{formatRupiah(stats.totalTagihan)}</td>
              </tr>
              <tr>
                <td className="summary-label">Total Tunggakan</td>
                <td className="summary-value text-danger">{formatRupiah(stats.totalTunggakan)}</td>
              </tr>
              <tr>
                <td className="summary-label">Mahasiswa Sudah Bayar</td>
                <td className="summary-value">{stats.jumlahMahasiswaBayar} mahasiswa</td>
              </tr>
              <tr>
                <td className="summary-label">Mahasiswa Belum Bayar</td>
                <td className="summary-value">{stats.jumlahMahasiswaBelumBayar} mahasiswa</td>
              </tr>
              <tr className="summary-total">
                <td className="summary-label">Persentase Pelunasan</td>
                <td className="summary-value text-success">{stats.persentasePelunasan}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BaakLaporan;
